import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { ExpandMore, Forward, Send, Visibility } from '@mui/icons-material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Link,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import Spinner from '~/components/Common/Spinner';
import EmailFormModal from '~/components/Emails/EmailFormModal';

import { useResendInfiniteOnboardingEmail } from '~/hooks/apis/loyalty/useCustomerServiceRepAssignments';
import {
  INFINITE_ONBOARDING_NOTIFICATION_TYPE,
  useInfiniteOnboardingEmails,
} from '~/hooks/apis/loyalty/useInfiniteOnboardingEmails';
import useCurrentUser from '~/hooks/useCurrentUser';

import { type SentEmail, resendEmail } from '~/services/SailthruService';
import { formatDateLongMonthWithMeridiemShort } from '~/services/TimeService';

interface Props {
  customerId: string;
  customerEmail: string;
  /** Only Infinite members with an assigned rep can trigger a new onboarding email. */
  hasConciergeRep?: boolean;
}

function getEmailStatus(email: SentEmail) {
  if (email.bounced_at || email.dropped_at) {
    return 'failed';
  }

  if (email.delivered_at) {
    return 'success';
  }

  return 'sent';
}

export default function LuxLoyaltyInfiniteOnboardingEmails({
  customerId,
  customerEmail,
  hasConciergeRep = false,
}: Props) {
  const [expanded, setExpanded] = useState(true);
  const [openForwardModalId, setOpenForwardModalId] = useState<string | null>(null);
  const [retryingNotificationId, setRetryingNotificationId] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useCurrentUser();
  const {
    data: emails = [],
    isLoading,
    isError,
    refetch,
  } = useInfiniteOnboardingEmails({
    customerEmail,
    enabled: expanded,
  });
  const { mutateAsync: resendEmailAsync } = useMutation({
    mutationFn: resendEmail,
  });
  const resendInfiniteOnboardingEmailMutation = useResendInfiniteOnboardingEmail({
    onSuccess: async (data) => {
      const emailStatus = data.result.sideEffects.infiniteOnboardingEmail;

      if (emailStatus.status === 'sent') {
        enqueueSnackbar('Infinite onboarding email sent', { variant: 'success' });
        await refetch();
        return;
      }

      enqueueSnackbar(`Infinite onboarding email was not sent: ${emailStatus.reason}`, { variant: 'warning' });
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to send Infinite onboarding email: ${error.message}`, { variant: 'error' });
    },
  });

  useEffect(() => {
    if (isError) {
      enqueueSnackbar('Failed to load Infinite onboarding emails', { variant: 'error' });
    }
  }, [enqueueSnackbar, isError]);

  const handleResend = useCallback(
    async (email: SentEmail) => {
      if (getEmailStatus(email) !== 'failed') {
        enqueueSnackbar('Only failed Infinite onboarding emails can be retried', { variant: 'warning' });
        return;
      }

      if (retryingNotificationId === email.notification_id) {
        return;
      }

      try {
        setRetryingNotificationId(email.notification_id);
        await resendEmailAsync({ id: email.notification_id, email: customerEmail });
        enqueueSnackbar('Infinite onboarding email retry requested', { variant: 'success' });
        await refetch();
      } catch (error) {
        enqueueSnackbar('Failed to retry Infinite onboarding email', { variant: 'error' });
      } finally {
        setRetryingNotificationId(null);
      }
    },
    [customerEmail, enqueueSnackbar, refetch, resendEmailAsync, retryingNotificationId],
  );

  const columns = useMemo<Array<GridColDef<SentEmail>>>(
    () => [
      {
        field: 'notification_type',
        headerName: 'Type',
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
      },
      {
        field: 'status',
        headerName: 'Status',
        sortable: false,
        disableColumnMenu: true,
        flex: 0.75,
        valueGetter: (_value, row) => getEmailStatus(row),
      },
      {
        field: 'notification_id',
        headerName: 'Notification ID',
        sortable: false,
        disableColumnMenu: true,
        flex: 1.25,
      },
      {
        field: 'updated_at',
        headerName: 'Last Updated',
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        valueFormatter: (value) => formatDateLongMonthWithMeridiemShort(value),
      },
      {
        field: 'email',
        headerName: 'To',
        sortable: false,
        disableColumnMenu: true,
        flex: 1.25,
        renderCell: (params) => (
          <Link
            color="primary"
            target="_blank"
            rel="noreferrer"
            href={`/email/sent?email=${params.row.email}`}
            underline="hover"
          >
            {params.row.email}
          </Link>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        renderCell: (params) => {
          const isFailed = getEmailStatus(params.row) === 'failed';
          const isRetrying = retryingNotificationId === params.row.notification_id;

          return (
            <Box>
              {isFailed && (
                <Button
                  size="small"
                  startIcon={<Send />}
                  disabled={isRetrying}
                  onClick={() => handleResend(params.row)}
                >
                  Retry
                </Button>
              )}
              <Link
                color="primary"
                target="_blank"
                rel="noreferrer"
                href={`/email/render?id=${params.row.notification_id}`}
                underline="hover"
              >
                <Button size="small" startIcon={<Visibility />}>
                  View
                </Button>
              </Link>
              <Button size="small" startIcon={<Forward />} onClick={() => setOpenForwardModalId(params.row.id)}>
                Forward
              </Button>
            </Box>
          );
        },
      },
    ],
    [handleResend, retryingNotificationId],
  );

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      variant="outlined"
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={1} gap={2}>
          <Box display="flex" alignItems="center" minWidth={0}>
            <EmailOutlinedIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Infinite Onboarding Emails</Typography>
          </Box>
          {hasConciergeRep && (
            <Button
              variant="contained"
              size="small"
              startIcon={
                resendInfiniteOnboardingEmailMutation.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <EmailOutlinedIcon fontSize="small" />
                )
              }
              disabled={resendInfiniteOnboardingEmailMutation.isPending}
              onClick={(event) => {
                event.stopPropagation();
                resendInfiniteOnboardingEmailMutation.mutate({ customerId });
              }}
            >
              Send onboarding email
            </Button>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {isLoading && <Spinner size={32} />}
        {!isLoading && (
          <>
            <DataGrid
              rows={emails}
              columns={columns}
              getRowId={(row) => row.id}
              pageSizeOptions={[]}
              disableColumnFilter
              disableColumnMenu
              disableRowSelectionOnClick
              autoHeight
              hideFooter
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-row': {
                  minHeight: 50,
                  paddingTop: '8px',
                  paddingBottom: '8px',
                },
              }}
            />
            <EmailFormModal
              open={!!openForwardModalId}
              handleClose={() => setOpenForwardModalId(null)}
              notificationId={openForwardModalId}
              notificationType={INFINITE_ONBOARDING_NOTIFICATION_TYPE}
              initialEmail={user.email}
            />
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
